import { useCallback, useEffect, useState } from "react";
import styles from "./receiveShipping.module.css";
import { Button, FormControl, InputLabel, MenuItem, Select, TextField, Table, TableHead, TableContainer, Paper, TableBody, TableRow, Checkbox, Snackbar, Alert, InputAdornment, IconButton, TableCell, Chip } from "@mui/material";
import { CheckCircleOutline, ClearRounded, SearchOutlined } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import { authData, withAdminAuth } from "../../components/GetAuth";
import Loader from "../../components/Loader";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { StyledMainTableCell } from "../../components/TableComponent";
import { Calendar } from "react-feather";
import { DateRangePicker } from "rsuite";
import GHWHItemListCustomRow from "./components/GHWHItemListCustomRow";
import ConItemListCustomRowGH from "./components/ConItemListCustomRowGH";
import { collectIds, inWarehouseIds, pendingReceiveIds } from "./components/shipItemUtils";
import PageHeader from "../../components/PageHeader";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";

dayjs.extend(isBetween);

const fieldSx = {
  width: "100%",

  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "10px",
    backgroundColor: "#fff",
    fontSize: "14px",
    transition: "all .2s ease",

    "& fieldset": {
      borderColor: "#d8dbe2",
    },

    "&:hover fieldset": {
      borderColor: "#ef233c99",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#ef233c",
      borderWidth: "2px",
    },

    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(239,35,60,.12)",
    },
  },

  "& .MuiOutlinedInput-input": {
    padding: "13px 14px",
    fontSize: "14px",
    color: "#22252f",
  },

  "& .MuiSelect-select": {
    padding: "13px 14px !important",
    display: "flex",
    alignItems: "center",
  },

  "& .MuiInputLabel-root": {
    color: "#8a8f98",
    fontSize: "14px",
    transform: "translate(14px, 13px) scale(1)",
    transition: "all .2s ease",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#ef233c",
  },

  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.75)",
    backgroundColor: "#fff",
    padding: "0 6px",
    color: "#ef233c",
  },

  "& .MuiInputBase-input::placeholder": {
    color: "#b0b4bc",
    opacity: 1,
  },

  "& .MuiSvgIcon-root": {
    color: "#9ca3af",
  },

  "& .Mui-disabled": {
    WebkitTextFillColor: "#5e6278",
    backgroundColor: "#f8f9fb",
  },

  "& .MuiOutlinedInput-root.Mui-disabled fieldset": {
    borderColor: "#e5e7eb",
  },
};

// shippings.status flow: 1 in transit -> 4 at port -> 2 at warehouse -> 3 all items received
const CONTAINER_STATE = {
  1: { label: "In transit", color: "#2563eb" },
  4: { label: "At port", color: "#0891b2" },
  2: { label: "At warehouse", color: "#4f46e5" },
  3: { label: "Completed", color: "#16a34a" },
};

const toggleId = (list, id) => (list.includes(id) ? list.filter((i) => i !== id) : [...list, id]);

const filterBookings = (list, filterVars) => {
  let result = list;

  if (filterVars?.searchKey !== "") {
    const inputVal = filterVars.searchKey.toLowerCase();
    result = result.filter((item) => item?.booking_id?.toLowerCase().includes(inputVal));
  }

  if (filterVars?.date) {
    result = result.filter((i) => dayjs(i?.pickup_date).isBetween(dayjs(filterVars.date[0]).format("YYYY-MM-DD"), dayjs(filterVars.date[1]).format("YYYY-MM-DD"), "day", "[]"));
  }

  return result;
};

const DeliveryReceiveShipping = () => {
  const auth = authData();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialShippingId = parseInt(searchParams.get("shipping")) || 0;

  const [containerList, setContainerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [WHItems, setWHItems] = useState([]);
  const [containerItems, setContainerItems] = useState([]);
  const [shippingData, setShippingData] = useState(null);
  const [shippingId, setShippingId] = useState(initialShippingId);
  const [filterVars1, setFilterVars1] = useState({ searchKey: "", date: null });
  const [filterVars2, setFilterVars2] = useState({ searchKey: "", date: null });
  const [selected1, setSelected1] = useState([]);
  const [selected2, setSelected2] = useState([]);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const { websiteData } = useWebsite();
  useSiteBranding("Receive Shipping", websiteData);

  const { register, control, setValue } = useForm({
    mode: "onChange",
    defaultValues: {
      container_no: initialShippingId || "",
      shipping_from: "",
      shipping_to: "",
      consignee: "",
      warehouse_location: "",
    }
  });

  const headers = { Authorization: `Bearer ${auth?.api_token}` };
  const shipStatus = parseInt(shippingData?.status);
  const containerInTransit = shipStatus === 1;
  const containerAtPort = shipStatus === 4;
  const containerArrived = shipStatus === 2;
  const containerCompleted = shipStatus === 3;
  const containerState = CONTAINER_STATE[shipStatus];

  const showSnack = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  const fetchContainerList = useCallback(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/delivery/shipping-dd-list", { headers: { Authorization: `Bearer ${auth?.api_token}` } }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setContainerList(response.data.data);
      }
    });
  }, [auth?.api_token]);

  const fetchShipping = useCallback((id) => {
    if (!id) {
      return;
    }

    axios.get(process.env.REACT_APP_API_URL + "v1/delivery/shipping-details/" + id, { headers: { Authorization: `Bearer ${auth?.api_token}` } }).then((response) => {
      if (response.data.status === "SUCCESS" && response.data.data) {
        setShippingData(response.data.data);
      }
    });
  }, [auth?.api_token]);

  const fetchWHItems = useCallback(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/shippings/booking-list-ghwh", { headers: { Authorization: `Bearer ${auth?.api_token}` } }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setWHItems(response.data.data);
      }
    });
  }, [auth?.api_token]);

  const fetchContainerItems = useCallback((id) => {
    axios.get(process.env.REACT_APP_API_URL + "v1/delivery/booking-list-ship/" + id, { headers: { Authorization: `Bearer ${auth?.api_token}` } }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setContainerItems(response.data.data);
      }
    });
  }, [auth?.api_token]);

  useEffect(() => {
    fetchContainerList();
    fetchWHItems();
  }, [fetchContainerList, fetchWHItems]);

  useEffect(() => {
    setSelected1([]);
    setSelected2([]);

    if (shippingId) {
      fetchShipping(shippingId);
    } else {
      setShippingData(null);
    }
  }, [shippingId, fetchShipping]);

  // Keep the read-only header fields in sync with the loaded shipping
  useEffect(() => {
    if (!shippingData) {
      return;
    }

    setValue("shipping_from", shippingData.shipping_from);
    setValue("shipping_to", shippingData.shipping_to);
    setValue("shipping_date", dayjs(shippingData.shipping_date));
    setValue("eta_of_container", dayjs(shippingData.eta_of_container));
    setValue("consignee", shippingData.consignee);
    setValue("warehouse_location", shippingData?.warehouse_location);
  }, [shippingData, setValue]);

  useEffect(() => {
    // Items are listed from "at port" onwards; they can only be received once the container is at the warehouse
    if (shippingId && shippingData && (containerAtPort || containerArrived || containerCompleted)) {
      fetchContainerItems(shippingId);
    } else {
      setContainerItems([]);
    }
  }, [shippingId, shippingData, containerAtPort, containerArrived, containerCompleted, fetchContainerItems]);

  const cngContainer = (e) => {
    const id = parseInt(e?.target?.value) || 0;
    setShippingId(id);
    setSearchParams(id ? { shipping: id } : {}, { replace: true });
  }

  const refreshLists = () => {
    fetchWHItems();
    fetchShipping(shippingId);
    fetchContainerList();
  }

  const markContainer = (endpoint, successMsg) => {
    if (!shippingId) {
      return false;
    }
    setLoading(true);

    axios.get(process.env.REACT_APP_API_URL + endpoint + shippingId, { headers }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setShippingData(response.data.data);
        fetchContainerList();
        fetchWHItems();
        showSnack(successMsg);
      } else {
        showSnack(response.data.message || "Something went wrong.", "error");
      }
    }).catch(() => {
      setLoading(false);
      showSnack("Something went wrong.", "error");
    });
  }

  const runItemAction = (endpoint, data) => {
    if (!data?.item_ids?.length) {
      showSnack("No items to update.", "warning");
      return;
    }

    setLoading(true);

    axios.post(process.env.REACT_APP_API_URL + endpoint, data, { headers }).then((response) => {
      setLoading(false);

      if (response.data.status === "SUCCESS") {
        showSnack(response.data.message);
        setSelected1([]);
        setSelected2([]);
        refreshLists();
      } else {
        showSnack(response.data.message || "Something went wrong.", "error");
      }
    }).catch(() => {
      setLoading(false);
      showSnack("Something went wrong.", "error");
    });
  }

  const receivePort = () => markContainer("v1/delivery/shipping-port-received/", "Container received at port. All items are now \"Received At Port\".");
  const receiveWarehouse = () => markContainer("v1/delivery/shipping-received/", "Container received at warehouse. You can now receive items.");

  const warehouseReceive = (item_ids) => runItemAction("v1/delivery/item-received-new", { shpping_id: shippingId, item_ids });
  const warehouseRemove = (item_ids) => runItemAction("v1/delivery/item-removed-new", { item_ids });

  const WHFilterItems = filterBookings(WHItems, filterVars1);
  const containerFilterItems = filterBookings(containerItems, filterVars2);

  const selectedReceiveIds = collectIds(containerFilterItems, selected2, pendingReceiveIds, shippingData);
  const selectedRemoveIds = collectIds(WHFilterItems, selected1, inWarehouseIds, shippingData);
  const removableWHItems = WHFilterItems.filter((b) => inWarehouseIds(b, shippingData).length > 0);

  const bulkAction = (ids, action) => {
    if (selected2.length === 0) {
      showSnack("Please select booking first!!", "warning");
      return;
    }
    action(ids);
  }

  return (<>
    {loading && <Loader />}
    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Delivery" trail={[{ label: 'Receive Shipping' }]} />

      <div className={`${styles.MainCard}`}>
        <div className={`${styles.StepOneForm}`}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className={`${styles.FormRowFlex}`}>
              <div className={`${styles.ColContainer}`}>
                <div className={`${styles.FieldWrap}`}>
                  <FormControl fullWidth>
                    <InputLabel>Container*</InputLabel>
                    <Controller
                      control={control}
                      {...register("container_no")}
                      render={({ field }) => (<Select
                        label="Container*"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          cngContainer(e);
                        }}
                        sx={fieldSx}
                      >
                        {containerList?.map((val) => {
                          return (<MenuItem key={val?.value} value={val?.value}>{val?.label}</MenuItem>);
                        })}
                        {/* Keep a completed container (no longer in the list) selectable after reload */}
                        {shippingId > 0 && shippingData && !containerList?.some((val) => String(val?.value) === String(shippingId)) && (<MenuItem value={shippingId}>{shippingData?.reference_id || shippingId}</MenuItem>)}
                      </Select>)}
                    />
                  </FormControl>
                </div>
              </div>

              <div className={`${styles.ColField}`}>
                <Controller
                  control={control}
                  disabled
                  {...register("shipping_from")}
                  render={({ field }) => (<TextField {...field} sx={fieldSx} fullWidth label="Shipping From*" variant="outlined" />)}
                />
              </div>

              <div className={`${styles.ColField}`}>
                <Controller
                  control={control}
                  disabled
                  {...register("shipping_to")}
                  render={({ field }) => (<TextField {...field} sx={fieldSx} fullWidth label="Shipping To*" variant="outlined" />)}
                />
              </div>
            </div>

            <div className={`${styles.FormRowFlex}`} style={{ marginTop: 20 }}>
              <div className={`${styles.ColField}`}>
                <Controller
                  control={control}
                  disabled
                  {...register("shipping_date")}
                  render={({ field }) => (<DatePicker {...field} sx={fieldSx} minDate={dayjs()} label="Shipping Date" variant="outlined" />)}
                />
              </div>

              <div className={`${styles.ColField}`}>
                <Controller
                  control={control}
                  disabled
                  {...register("eta_of_container")}
                  render={({ field }) => (<DatePicker {...field} sx={fieldSx} minDate={dayjs()} label="Eta Of Container" />)}
                />
              </div>
              <div className={`${styles.ColField}`}>
                <Controller
                  control={control}
                  disabled
                  {...register("consignee")}
                  render={({ field }) => (<TextField {...field} sx={fieldSx} fullWidth label="Consignee*" variant="outlined" />)}
                />
              </div>
              <div className={`${styles.ColField}`}>
                <Controller
                  control={control}
                  disabled
                  {...register("warehouse_location")}
                  render={({ field }) => (<TextField {...field} sx={fieldSx} fullWidth label="Warehouse Location*" variant="outlined" />)}
                />
              </div>
            </div>
            <div className={`${styles.StepBUSec}`}>
              {containerState && (<Chip label={containerState.label} size="small" sx={{ background: containerState.color, color: "#fff", fontWeight: 600 }} />)}
              {shippingData?.port_arrival_date && (<span className={styles.ArrivalText}>At port on {dayjs(shippingData.port_arrival_date).format("DD/MM/YYYY")}</span>)}
              {(containerArrived || containerCompleted) && shippingData?.arrival_date && (<span className={styles.ArrivalText}>At warehouse on {dayjs(shippingData.arrival_date).format("DD/MM/YYYY")}</span>)}
              {shippingData && (<div className={styles.ContainerActions}>
                <button type="button" className={`${styles.ContainerBU} ${styles.PortBU}`} onClick={receivePort} disabled={!containerInTransit}>
                  {containerInTransit ? "Mark Container Received at Port" : shippingData?.port_arrival_date ? (<><CheckCircleOutline fontSize="small" />Received at Port</>) : "Port skipped"}
                </button>
                <button type="button" className={`${styles.ContainerBU} ${styles.WarehouseBU}`} onClick={receiveWarehouse} disabled={!(containerInTransit || containerAtPort)}>
                  {(containerArrived || containerCompleted) ? (<><CheckCircleOutline fontSize="small" />Received at Warehouse</>) : "Mark Container Received at Warehouse"}
                </button>
              </div>)}
            </div>
          </LocalizationProvider>
        </div>
      </div >

      <div className={`${styles.MainCard}`} style={{ background: 'none', padding: 0, border: 'none', boxShadow: 'none' }}>
        <div className={`${styles.MainCard2Half} ${styles.MainCard2Half1}`}>
          <p>Container Details</p>
          <span className={styles.FlowHint}>Mark the container at port, then at warehouse, then receive items into the warehouse to assign a delivery driver.</span>

          <div className={`${styles.TableFilterHead}`}>
            <div className={`${styles.SearchBox}`}>
              <TextField
                fullWidth
                placeholder="Search booking id..."
                value={filterVars2?.searchKey}
                onChange={(e) => setFilterVars2((prev) => { return { ...prev, searchKey: e.target.value }; })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined className={styles.SearchIcon} />
                    </InputAdornment>
                  ),
                  endAdornment: filterVars2?.searchKey && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={(e) => setFilterVars2((prev) => { return { ...prev, searchKey: "" }; })}>
                        <ClearRounded fontSize="small" className={styles.ClearButton} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className={styles.DateFilterWrap}>
              <DateRangePicker
                format={"dd/MM/yyyy"}
                placement={"bottomEnd"}
                placeholder="Search by date"
                caretAs={Calendar}
                value={filterVars2?.date}
                showOneCalendar
                onChange={(e) => setFilterVars2((prev) => { return { ...prev, date: e }; })}
              />
            </div>

            <div className={`${styles.FilerButton}`}>
              <Button className={`${styles.TableAddBU}`} disabled={!containerArrived || selectedReceiveIds.length === 0} onClick={() => bulkAction(selectedReceiveIds, warehouseReceive)}>
                Receive Selected{selectedReceiveIds.length > 0 ? ` (${selectedReceiveIds.length})` : ""}
              </Button>
            </div>
          </div>

          <div className={`${styles.CusTable}`}>
            <TableContainer component={Paper} elevation={0}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <StyledMainTableCell padding="checkbox" style={{ padding: '12px 0', width: '34px' }}>
                      <Checkbox
                        className={`${styles.AllCheckbox}`}
                        color="primary"
                        indeterminate={selected2?.length > 0 && selected2?.length < containerFilterItems?.length}
                        checked={containerFilterItems?.length > 0 && selected2?.length === containerFilterItems?.length}
                        onChange={(e) => setSelected2(e.target.checked ? containerFilterItems.map((n) => n.id) : [])}
                        disabled={!containerArrived}
                      />
                    </StyledMainTableCell>
                    <StyledMainTableCell style={{ padding: '12px 1px', textAlign: 'center' }}>Booking Id</StyledMainTableCell>
                    <StyledMainTableCell style={{ padding: '12px 1px', textAlign: 'center' }}>Pickup Date</StyledMainTableCell>
                    <StyledMainTableCell className={`${styles.TableColumn1}`} style={{ padding: '12px 2px', textAlign: 'center' }}>Items</StyledMainTableCell>
                    <StyledMainTableCell className={`${styles.TableColumn2}`} style={{ padding: '12px 2px', textAlign: 'center' }}>Items</StyledMainTableCell>
                    <StyledMainTableCell className={`${styles.TableColumn2}`} style={{ padding: '12px 2px', textAlign: 'center' }}>In Container</StyledMainTableCell>
                    <StyledMainTableCell className={`${styles.TableColumn2}`} style={{ padding: '12px 2px', textAlign: 'center' }}>At Port</StyledMainTableCell>
                    <StyledMainTableCell></StyledMainTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {containerFilterItems.length === 0 && (<TableRow>
                    <TableCell colSpan={10} align="center">{!shippingId ? "Select a container" : containerInTransit ? "Mark the container received at port to continue" : "No data found"}</TableCell>
                  </TableRow>)}
                  {containerFilterItems?.map((item) => {
                    const isItemSelected = selected2.indexOf(item?.id) !== -1;
                    return (<ConItemListCustomRowGH
                      key={item?.id}
                      item={item}
                      isItemSelected={isItemSelected}
                      shippingData={shippingData}
                      canAct={containerArrived}
                      selectRowEvent={(id) => setSelected2((prev) => toggleId(prev, id))}
                      receiveItemNew={warehouseReceive}
                    />);
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>

        <div className={`${styles.MainCard2Half}  ${styles.MainCard2Half2}`}>
          <p>Item In GH Warehouse</p>
          <span className={styles.FlowHint}>Received in the warehouse and ready to assign to a delivery driver.</span>

          <div className={`${styles.TableFilterHead}`}>
            <div className={`${styles.SearchBox}`}>
              <TextField
                fullWidth
                placeholder="Search booking id..."
                value={filterVars1?.searchKey}
                onChange={(e) => setFilterVars1((prev) => { return { ...prev, searchKey: e.target.value }; })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined className={styles.SearchIcon} />
                    </InputAdornment>
                  ),
                  endAdornment: filterVars1?.searchKey && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={(e) => setFilterVars1((prev) => { return { ...prev, searchKey: "" }; })}>
                        <ClearRounded fontSize="small" className={styles.ClearButton} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className={styles.DateFilterWrap}>
              <DateRangePicker
                format={"dd/MM/yyyy"}
                placement={"bottomEnd"}
                placeholder="Search by date"
                caretAs={Calendar}
                value={filterVars1?.date}
                showOneCalendar
                onChange={(e) => setFilterVars1((prev) => { return { ...prev, date: e }; })}
              />
            </div>

            <div className={`${styles.FilerButton}`}>
              <Button className={`${styles.TableAddBU}`} disabled={selectedRemoveIds.length === 0} onClick={() => warehouseRemove(selectedRemoveIds)}>
                Remove{selectedRemoveIds.length > 0 ? ` (${selectedRemoveIds.length})` : ""}
              </Button>
            </div>
          </div>

          <div className={`${styles.CusTable}`}>
            <TableContainer component={Paper} elevation={0}>
              <Table sx={{ minWidth: 650 }} aria-label="collapsible table">
                <TableHead>
                  <TableRow>
                    <StyledMainTableCell padding="checkbox" style={{ padding: '12px 0', width: '34px' }}>
                      <Checkbox
                        className={`${styles.AllCheckbox}`}
                        color="primary"
                        disabled={removableWHItems.length === 0}
                        indeterminate={selected1?.length > 0 && selected1?.length < removableWHItems?.length}
                        checked={removableWHItems?.length > 0 && selected1?.length === removableWHItems?.length}
                        onChange={(e) => setSelected1(e.target.checked ? removableWHItems.map((n) => n.id) : [])}
                      />
                    </StyledMainTableCell>
                    <StyledMainTableCell style={{ padding: '12px 1px', textAlign: 'center' }}>Booking Id</StyledMainTableCell>
                    <StyledMainTableCell style={{ padding: '12px 1px', textAlign: 'center' }}>Pickup Date</StyledMainTableCell>
                    <StyledMainTableCell className={`${styles.TableColumn1}`} style={{ padding: '12px 2px', textAlign: 'center' }}>Items</StyledMainTableCell>
                    <StyledMainTableCell className={`${styles.TableColumn2}`} style={{ padding: '12px 2px', textAlign: 'center' }}>Items</StyledMainTableCell>
                    <StyledMainTableCell className={`${styles.TableColumn2}`} style={{ padding: '12px 2px', textAlign: 'center' }}>Received</StyledMainTableCell>
                    <StyledMainTableCell className={`${styles.TableColumn2}`} style={{ padding: '12px 2px', textAlign: 'center' }}>Remaining</StyledMainTableCell>
                    <StyledMainTableCell></StyledMainTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {WHFilterItems.length === 0 && (<TableRow>
                    <TableCell colSpan={10} align="center">No data found</TableCell>
                  </TableRow>)}
                  {WHFilterItems?.map((item) => {
                    const isItemSelected = selected1.indexOf(item?.id) !== -1;
                    return (<GHWHItemListCustomRow key={item?.id} item={item} isItemSelected={isItemSelected} selectRowEvent={(id) => setSelected1((prev) => toggleId(prev, id))} removeItemNew={warehouseRemove} shippingData={shippingData} />);
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>
    </div>

    <Snackbar open={snack.open} onClose={(event, reason) => { if (reason !== "clickaway") setSnack((prev) => ({ ...prev, open: false })); }} anchorOrigin={{ vertical: "top", horizontal: "right" }} autoHideDuration={3000}>
      <Alert onClose={() => setSnack((prev) => ({ ...prev, open: false }))} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>{snack.msg}</Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(DeliveryReceiveShipping);
